<template>
  <div style="flex: 1 1 0px">
    <v-dialog
      v-model="dialog"
      width="1200"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <template v-slot:activator="{ on, attrs }">
        <div class="stat-card" v-on="on" v-bind="attrs">
          <div class="stat-card__top">
            <div class="stat-card__number">
              <v-progress-circular
                v-if="loading"
                indeterminate
                size="24"
                width="2"
                color="accent"
              ></v-progress-circular>
              <span v-else>{{ inquiries.length }}</span>
            </div>
            <div class="stat-card__icon">
              <hero-icon :icon="cardIcon" :size="20" />
            </div>
          </div>

          <div class="stat-card__label">
            {{ status[0] == "success" ? "Consultas finalizadas" : "Consultas en curso" }}
          </div>
        </div>
      </template>

      <v-card class="rounded-lg dialog-card">
        <div class="dialog-header">
          <div class="dialog-heading">
            <div class="dialog-title">Consultas</div>
            <div class="dialog-subtitle">
              {{ status[0] == "success" ? "Finalizadas" : "En curso" }}
            </div>
          </div>

          <button type="button" class="ghost-close" @click="dialog = false">
            <hero-icon :icon="XMark" :size="18" />
          </button>
        </div>

        <div class="dialog-divider"></div>

        <div class="dialog-body">
          <div v-if="loading" class="dialog-state">
            <v-progress-circular
              indeterminate
              size="32"
              width="3"
              color="accent"
            ></v-progress-circular>
          </div>

          <div v-else-if="loaded && inquiries.length === 0" class="dialog-state empty-state">
            Sin consultas
          </div>

          <template v-else>
            <card
              v-for="inquiry in inquiries"
              :key="inquiry.id"
              :inquiry="inquiry"
              @reload="reloadInquiries"
              @open-property="openProperty"
            ></card>
          </template>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Inquiries } from "@/graphql/queries/inquiries";

import Card from "@/components/inquiries/Card";
import HeroIcon from "@/components/shared/HeroIcon";

import CheckCircle from "heroicons/24/outline/check-circle.svg";
import ChatBubble from "heroicons/24/outline/chat-bubble-left-right.svg";
import XMark from "heroicons/24/outline/x-mark.svg";

export default {
  data: () => ({
    dialog: false,
    inquiries: [],
    loading: false,
    loaded: false,
    CheckCircle,
    ChatBubble,
    XMark
  }),

  props: {
    id: {
      required: true,
      type: Number | String
    },
    status: {
      required: true,
      type: Array
    }
  },

  computed: {
    cardIcon() {
      return this.status[0] == "success" ? this.CheckCircle : this.ChatBubble;
    }
  },

  watch: {
    id: {
      handler() {
        // Reset cache for the new staff member, then refresh the count.
        this.loaded = false;
        this.inquiries = [];
        this.fetchInquiries();
      }
    },

    dialog(open) {
      // Lazy-load the heavy list on first open only; guarded so we never
      // double-fetch on open/close/reopen.
      if (open && !this.loaded && !this.loading) {
        this.fetchInquiries();
      }
    }
  },

  created() {
    // A count must be available on the card without opening the dialog.
    this.fetchInquiries();
  },

  methods: {
    fetchInquiries(fetchPolicy = "cache-first") {
      this.loading = true;

      return this.$apollo
        .query({
          query: Inquiries,
          fetchPolicy: fetchPolicy,
          variables: {
            userId: this.id,
            status: this.status
          }
        })
        .then(res => {
          this.inquiries = res.data.inquiries.inquiries;
          this.loaded = true;
          this.loading = false;
        });
    },

    // @reload handler from the inquiry Card: force a fresh refetch after a
    // mutation so the open dialog reflects the edit.
    reloadInquiries() {
      this.loaded = false;
      this.fetchInquiries("network-only");
    },

    // Inside this modal a fixed side-drawer would render behind the dialog, so
    // keep the property shortcut opening in a new tab here.
    openProperty(property) {
      const path = this.$router.resolve({
        name: "Property",
        params: { id: property.id }
      });
      window.open(path.href, "_blank");
    }
  },

  components: { Card, HeroIcon }
};
</script>

<style scoped>
/* ===== Canonical Gestiones stat card (shared across all four analytics cards) ===== */
.stat-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  height: 100%;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
  cursor: pointer;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.stat-card:hover {
  border-color: #dcd4c6;
  box-shadow: 0 12px 28px -16px rgba(40, 33, 22, 0.24);
  transform: translateY(-1px);
}

.stat-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.stat-card__number {
  display: flex;
  align-items: center;
  min-height: 30px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  color: #1a1814;
}

.stat-card__icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #faf8f4;
  color: #b52118;
}

.stat-card__label {
  font-size: 13.5px;
  color: #756f64;
}

.dialog-card {
  background: #ffffff;
}

.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px;
}

.dialog-heading {
  display: flex;
  flex-direction: column;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1814;
}

.dialog-subtitle {
  margin-top: 2px;
  font-size: 13px;
  color: #756f64;
}

.ghost-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #756f64;
  transition: background 0.15s ease, color 0.15s ease;
}

.ghost-close:hover {
  background: #f1ece3;
  color: #1a1814;
}

.dialog-divider {
  height: 1px;
  background: #ece7de;
}

.dialog-body {
  padding: 16px 20px 20px;
  max-height: 72vh;
  overflow-y: auto;
}

.dialog-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.empty-state {
  font-size: 14px;
  color: #a39a8c;
}

@media (max-width: 960px) {
  .dialog-body {
    max-height: none;
  }
}
</style>
